import { apiSecurityPinOpenVerify, apiSecurityPinSimpleRegistration, apiSecurityPinVerify } from '@api/apiCall';
import { fetchSecurityPinOpen } from '@api/useQueryCalls';
import { securityPin as pinImage } from '@assets/png/imagesPng';
import LocalImage from '@atoms/LocalImage';
import { Button, Text, View } from '@components/primitives';
import useFeatureFlag from '@customHooks/useFeatureFlag';
import LoginInfoSheet from '@molecules/LoginInfoSheet';
import navigationConstants from '@navigation/navigationConstants';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import { getStore } from '@store/storeUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import OTPInputView from '@twotalltotems/react-native-otp-input';
import { scale } from '@utils/normalize';
import { returnBottomSheetContent, showErrorOnAPI } from '@utils/utils';
// import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { logHelpClicked, logOnPage } from './analyticsCreatePiin';
import {
  MODAL_KEYS,
  getModalKeyBasedOnFeatureFlags,
  getTitleName,
  otherPinHandlers,
  phoneChangeEnrolledPinStart,
  pinHandlerFunctions,
  shoulRenderInstructions as shouldRenderInstructions,
  shouldRenderOnOpenPinError,
  shouldRenderOnVerifyOpenSuccess,
  shouldRenderOnVerifyPinError,
  shouldRenderOnVerifyPinSuccess,
  shouldRenderSuccessPinCreation,
  shouldShowErrorText,
  verifyAndResetPinHandlers,
} from './pinUtils';
import styles from './styles';
import { useToast } from '@root/hooks/useToast';

type RootStackParamList = {
  [navigationConstants.GG_CREATE_PIN]: {
    type: 'create';
    source?: string;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
  [navigationConstants.GG_VERIFY_PIN]: {
    type: 'verifyPin';
    source?: string;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
  [navigationConstants.GG_VERIFY_PIN_WITH_EXPIRY]: {
    type: 'verifyPinWithExpiry';
    source?: string;
    onVerifyCallBack?: () => void;
    onVerifyValues?: Record<string, any> | Array<any>;
  };
};

type SecurityPinRouteProp = RouteProp<RootStackParamList>;

export interface SecurityPinOpenResponse {
  attempts: number;
  remainingAttempts: number;
  token: string;
}

const GGCreatePin: React.FC = () => {
  const [otpNew, setOtp] = useState('');
  const [confirmCopy, setConfirmCopy] = useState('');
  const [invalidConfirms, setInvalidConfirms] = useState(0);
  const [clearInputs, setClearInputs] = useState(false);
  const [errTxtMsg, setErrTxtMsg] = useState('');
  const [verifyInvalid, setVerifyInvalid] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const route = useRoute<SecurityPinRouteProp>();
  const navigation = useNavigation();
  const { t } = useTranslation('common');
  const [isFeatureAvailale] = useFeatureFlag({
    featureName: [FEATURE_FLAGS.BANK_SEL_TRNSF, FEATURE_FLAGS.PHONE_ENROLL, FEATURE_FLAGS.PHONE_NUMBER_CHANGE],
  });
  const {
    pinRegister: securityPin,
    setLoading,
    setShowPinRegister,
    securityPinOpen,
    setSecurityPinOpen,
    setInitialData,
  } = getStore();

  const {
    isLoading,
    isSuccess,
    isError: isErrorCreate,
    error: errorCreate,
    data,
    mutate: securityPinRegistration,
  } = useMutation({
    mutationFn: payload => apiSecurityPinSimpleRegistration(payload),
  });

  // Verify Pin (i.e Phone & Bank Enrollment)
  const {
    isLoading: isLoadingVerify,
    isSuccess: isSuccessVerify,
    data: dataVerify,
    isError: isErrorVerify,
    error: errorVerify,
    mutate: securityPinVerify,
  } = useMutation({ mutationFn: payloadVerify => apiSecurityPinVerify(payloadVerify) });

  // Verify Pin with Expiry (i.e Phone Number Change)
  const {
    isLoading: isLoadingOpenVerify,
    isSuccess: isSuccessOpenVerify,
    data: dataOpenVerify,
    isError: isErrorOpenVerify,
    error: errorOpenVerify,
    mutate: securityPinOpenVerify,
  } = useMutation({ mutationFn: payloadVerify => apiSecurityPinOpenVerify(payloadVerify) });

  const sourceParam = route.params?.source;
  let typeScreenSetter = route.params?.type;
  const { onVerifyCallBack, onVerifyValues } = route.params;
  if (route.name === navigationConstants.GG_CREATE_PIN) {
    typeScreenSetter = 'create';
  } else if (route.name === navigationConstants.GG_VERIFY_PIN) {
    typeScreenSetter = 'verifyPin';
  } else if (route.name === navigationConstants.GG_VERIFY_PIN_WITH_EXPIRY) {
    typeScreenSetter = 'verifyPinWithExpiry';
  }

  const [typeScreen, setTypeScreen] = useState<'create' | 'confirm' | 'verifyPin' | 'verifyPinWithExpiry'>(
    typeScreenSetter,
  );

  const otpRef = useRef(null);
  const [disableOTP, setDisableOTP] = useState(false);

  const [infoModal, setInfoModal] = useState({
    visible: false,
    data: returnBottomSheetContent(
      SECURITY_PIN_INFO_MODAL[MODAL_KEYS.GG_01_FALLBACK],
      SECURITY_PIN_INFO_MODAL[MODAL_KEYS.GG_01_FALLBACK],
    ),
    errorCode: MODAL_KEYS.GG_01_FALLBACK,
  });

  const queryPinOpen = fetchSecurityPinOpen({
    payload: {
      screenName: 'changePhoneNumber',
    },
  });

  const { errorHandler, onCodeChangedHandler, onPrimaryPress, onPressSecondaryHandler, onCloseModal } =
    pinHandlerFunctions({
      setOtp,
      setVerifyInvalid,
      errTxtMsg,
      setErrTxtMsg,
      setClearInputs,
      route,
      navigation,
      setInfoModal,
      infoModal,
      setTypeScreen,
      MODAL_KEYS,
      onVerifyCallBack,
      onVerifyValues,
    });

  const getModalNameKey = getModalKeyBasedOnFeatureFlags(isFeatureAvailale, sourceParam)();

  const {
    verifyPinHandler,
    verifyPinErrorHandling,
    resetPinHandler,
    verifyPinOpenHandler,
    verifyPinOpenErrorHandling,
  } = verifyAndResetPinHandlers({
    securityPin,
    setShowPinRegister,
    setVerifyInvalid,
    route,
    t,
    errorHandler,
    setInfoModal,
    securityPinVerify,
    securityPinOpenVerify,
    securityPinOpen,
    setSecurityPinOpen,
    queryClient,
  });
  const { getPageNumber, onNextPress, onCodeFilledHandler } = otherPinHandlers({
    typeScreen,
    route,
    verifyPinHandler,
    verifyPinOpenHandler,
    setDisableOTP,
    confirmCopy,
    invalidConfirms,
    setInfoModal,
    setInvalidConfirms,
    errorHandler,
    setConfirmCopy,
    otpNew,
    setTypeScreen,
    securityPinRegistration,
    t,
    securityPinOpen,
  });

  useEffect(() => {
    logOnPage(getPageNumber, route);
  }, [route.params]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    shouldRenderSuccessPinCreation(
      isSuccess,
      data,
      setInfoModal,
      getModalNameKey,
      setShowPinRegister,
      sourceParam,
      queryPinOpen,
    );
  }, [isSuccess, data]);

  // Handling of after Security Pin enroll before navigate to Phone Change Screen
  useEffect(() => {
    if (
      typeScreenSetter === 'create' &&
      sourceParam === 'phone_change' &&
      queryPinOpen.isSuccess &&
      queryPinOpen.data
    ) {
      const _data = queryPinOpen.data as unknown as SecurityPinOpenResponse;
      phoneChangeEnrolledPinStart(otpNew, _data?.token, securityPinOpenVerify);
    }
  }, [queryPinOpen.isSuccess, queryPinOpen.data]);

  // Verify Pin with Expiry (i.e Phone Number Change)
  useEffect(() => {
    if (queryPinOpen.isSuccess && queryPinOpen.data) {
      const _data = queryPinOpen.data as unknown as SecurityPinOpenResponse;
      setSecurityPinOpen({
        attempts: _data?.attempts,
        remainingAttempts: _data?.remainingAttempts,
        token: _data?.token,
      });
    }
    setLoading(false);
  }, [queryPinOpen.isSuccess, queryPinOpen.data]);

  useEffect(() => {
    setLoading(isLoadingVerify);
  }, [isLoadingVerify]);

  useEffect(() => {
    if (typeScreenSetter === 'create' && sourceParam === 'phone_change') {
      return;
    }
    setLoading(isLoadingOpenVerify);
  }, [isLoadingOpenVerify]);

  useEffect(() => {
    showErrorOnAPI(isErrorCreate, errorCreate, toast, t, navigation, setInitialData);
  }, [isErrorCreate, errorCreate]);

  useEffect(() => {
    shouldRenderOnVerifyPinError(isErrorVerify, errorVerify, verifyPinErrorHandling);
  }, [isErrorVerify, errorVerify]);

  useEffect(() => {
    shouldRenderOnOpenPinError(queryPinOpen.isError, queryPinOpen.error, verifyPinOpenErrorHandling);
  }, [queryPinOpen.isError, queryPinOpen.error]);

  useEffect(() => {
    shouldRenderOnVerifyPinError(isErrorOpenVerify, errorOpenVerify, verifyPinOpenErrorHandling);
  }, [isErrorOpenVerify, errorOpenVerify]);

  useEffect(() => {
    shouldRenderOnVerifyPinSuccess(
      isSuccessVerify,
      dataVerify,
      route,
      onVerifyCallBack,
      onVerifyValues,
      otpNew,
      setShowPinRegister,
    );
  }, [isSuccessVerify, dataVerify]);

  useEffect(() => {
    shouldRenderOnVerifyOpenSuccess(isSuccessOpenVerify, dataOpenVerify, route, onVerifyCallBack, setShowPinRegister);
  }, [isSuccessOpenVerify, dataOpenVerify]);

  function shouldRenderButton(show) {
    if (show)
      return (
        <Button
          paddingY={scale(8)}
          testId="lanjutkanButton"
          onPress={onNextPress}
          isDisabled={disableOTP || otpNew.length < 6}>
          {t('common.continue')}
        </Button>
      );
    return null;
  }

  const onHelpHandler = () => {
    resetPinHandler('link');
    logHelpClicked();
  };

  function shouldRenderResetLink(show) {
    if (show)
      return (
        <View flex={1}>
          <View flex={1} />
          <Pressable onPress={onHelpHandler}>
            <Text variant="sm-bold" color={colors.secondary.orchid} alignSelf="center" underline>
              {t('securityPin.resetLinkText')}
            </Text>
          </Pressable>
        </View>
      );

    return null;
  }

  return (
    <View style={styles.createPinContainer}>
      <View style={appStyles.safeAreaView}>
        <LocalImage style={styles.pinImageContainer} source={pinImage} />
        <Text style={styles.pinTitleContainer} variant="lg-bold">
          {getTitleName(typeScreen, t)}
        </Text>
        <OTPInputView
          pinCount={6}
          ref={otpRef}
          code={otpNew}
          style={styles.otpInputContainer}
          clearInputs={clearInputs}
          autoFocusOnLoad={false}
          onCodeFilled={onCodeFilledHandler}
          codeInputFieldStyle={[styles.otpDefault, verifyInvalid && styles.otpFailed]}
          secureTextEntry
          onCodeChanged={onCodeChangedHandler}
          codeInputHighlightStyle={[styles.otpHightLight]}
        />
        {shouldShowErrorText(errTxtMsg)}
        {shouldRenderInstructions(typeScreen === 'create' || typeScreen === 'confirm', isFeatureAvailale, t)}
        {shouldRenderResetLink(typeScreen === 'verifyPin')}
        <LoginInfoSheet
          onPressPrimary={onPrimaryPress}
          onPressSecondary={onPressSecondaryHandler}
          loginInfo={infoModal}
          onClose={onCloseModal}
        />
      </View>
      <View style={styles.btnVerifyCreate}>
        {shouldRenderButton(typeScreen === 'create' || typeScreen === 'confirm')}
      </View>
    </View>
  );
};

// GGCreatePin.propTypes = {};
// GGCreatePin.defaultProps = {};
export default GGCreatePin;
